import { useFieldArray, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { volumesOptions } from "@/constants/options/volumes.option";
import TrashIcon from "@/assets/icons/trash.svg?react";
import ShipmentTransitIcon from "@/assets/icons/shipment-transit.svg?react";
import ShieldIcon from "@/assets/icons/shield.svg?react";
import { StyledSelectSingleValueInt } from "@/components/styled/StyledSelectSingleValueInt";
import StyledSwitch from "@/components/ui/customs/StyledSwitch";
import { CustomCotationFormData } from "@/types/forms/home/customCotationsForm.form";
import { ControlledInputV2 } from "../ui/customs/controlled-input-v2";

export const InfoProductsPallet = () => {
  const { t } = useTranslation("boQuotations");
  const methods = useFormContext<CustomCotationFormData>();

  const productsFieldArray = useFieldArray({
    control: methods.control,
    name: "paletteProducts",
  });

  const packagesFieldArray = useFieldArray({
    control: methods.control,
    name: "palettePackages",
  });

  const removeFromArr = (index: number) => {
    productsFieldArray.remove(index);
  };

  return (
    <div className="flex w-full flex-1 flex-col items-center gap-6">
      <div className="shadow-basic flex w-full max-w-[800px] flex-col items-center justify-center rounded-xl border border-gray-200 bg-white p-6">
        <div className="flex flex-col items-center justify-center gap-4">
          <div className="flex w-full flex-col items-center justify-center gap-2">
            <h2 className="text-brand700 text-xl font-semibold">{t("productsPallet.title")}</h2>
          </div>
          <div className="flex w-full flex-col items-end gap-2">
            {productsFieldArray.fields.map((fields, index) => (
              <div className="flex w-full flex-col items-start gap-2" key={fields.id}>
                <p className="font-semibold text-gray-700">
                  {t("productsPallet.productTitle", { number: index + 1 })}
                </p>
                <div className="flex w-full flex-col items-start gap-2 md:flex-row">
                  <div className="flex w-full flex-1 flex-col items-start sm:flex-row sm:gap-2">
                    <StyledSelectSingleValueInt
                      options={volumesOptions}
                      setValue={(e: any) => {
                        methods.setValue(`paletteProducts.${index}.volume`, e);
                        methods.trigger([`paletteProducts.${index}.volume`]);
                      }}
                      control={methods.control}
                      name={`paletteProducts.${index}.volume`}
                      className="w-full"
                      title={t("productsPallet.volumeLabel")}
                    />
                    <div className="flex w-full flex-row items-start gap-2">
                      <ControlledInputV2
                        label={t("productsPallet.bottleQuantityLabel")}
                        control={methods.control}
                        name={`paletteProducts.${index}.bottleQuantity`}
                        className="w-full"
                        type="number"
                        disableDecimal
                      />
                      {productsFieldArray.fields.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeFromArr(index)}
                          className="mt-[15px] flex items-start justify-center hover:bg-gray-100 focus:outline-hidden"
                        >
                          <TrashIcon className="cursor-pointer text-red-600" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={() => productsFieldArray.append({ volume: 0, bottleQuantity: 0 })}
              className="bg-brand50 text-brand700 hover:border-brand600 h-fit w-fit rounded-lg px-4 py-3 text-base font-semibold focus:outline-hidden"
            >
              {t("productsPallet.addProduct")}
            </button>
          </div>
          <h2 className="text-brand700 text-xl font-semibold">
            {t("productsPallet.dimensionsTitle")}
          </h2>
          <div className="flex w-full flex-col items-end gap-2">
            {packagesFieldArray.fields.map((field, index) => (
              <div className="flex flex-col items-start gap-2" key={field.id}>
                <p className="font-semibold text-gray-700">
                  {t("productsPallet.paletteTitle", { number: index + 1 })}
                </p>
                <div className="flex flex-col items-start gap-2 md:flex-row">
                  <div className="flex flex-row items-start gap-2">
                    <ControlledInputV2
                      label={t("productsPallet.lengthLabel")}
                      control={methods.control}
                      name={`palettePackages.${index}.length`}
                      className="w-full"
                      rightElement={<p className="text-xs text-gray-600">cm</p>}
                      type="number"
                      disableDecimal
                    />
                    <ControlledInputV2
                      label={t("productsPallet.widthLabel")}
                      control={methods.control}
                      name={`palettePackages.${index}.width`}
                      className="w-full"
                      rightElement={<p className="text-xs text-gray-600">cm</p>}
                      type="number"
                      disableDecimal
                    />
                  </div>
                  <div className="flex flex-row items-start gap-2">
                    <ControlledInputV2
                      label={t("productsPallet.heightLabel")}
                      control={methods.control}
                      name={`palettePackages.${index}.height`}
                      className="w-full"
                      rightElement={<p className="text-xs text-gray-600">cm</p>}
                      type="number"
                      disableDecimal
                    />
                    <ControlledInputV2
                      label={t("productsPallet.weightLabel")}
                      control={methods.control}
                      name={`palettePackages.${index}.weight`}
                      className="w-full"
                      tooltip={t("productsPallet.weightTooltip")}
                      rightElement={<p className="text-xs text-gray-600">kg</p>}
                      type="number"
                      disableDecimal
                    />
                  </div>
                </div>
                <div className="flex w-full flex-row items-start gap-2">
                  <ControlledInputV2
                    label={t("productsPallet.valueLabel")}
                    control={methods.control}
                    name={`palettePackages.${index}.value`}
                    className="w-full"
                    rightElement={<p>€</p>}
                    tooltip={t("productsPallet.valueTooltip")}
                    type="number"
                  />
                  {packagesFieldArray.fields.length > 1 && (
                    <button
                      type="button"
                      onClick={() => packagesFieldArray.remove(index)}
                      className="mt-[15px] flex items-start justify-center hover:bg-gray-100 focus:outline-hidden"
                    >
                      <TrashIcon className="cursor-pointer text-red-600" />
                    </button>
                  )}
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={() =>
                packagesFieldArray.append({
                  value: 0,
                  length: 0,
                  width: 0,
                  height: 0,
                  weight: 0,
                })
              }
              className="bg-brand50 text-brand700 hover:border-brand600 h-fit w-fit rounded-lg px-4 py-3 text-base font-semibold focus:outline-hidden"
            >
              {t("productsPallet.addPalette")}
            </button>
          </div>
        </div>
      </div>
      <div className="shadow-basic flex w-full max-w-[800px] flex-col items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white p-6">
        <div className="flex w-full flex-1 flex-row items-center justify-between gap-4">
          <div className="flex flex-1 flex-row items-center justify-between gap-4">
            <ShieldIcon className="text-zinc-900" />
            <div className="flex flex-1 flex-col">
              <p className="font-semibold">{t("productsPallet.insuranceTitle")}</p>
              <p className="hidden md:block">{t("productsPallet.insuranceDescription")}</p>
            </div>
          </div>
          <StyledSwitch name="insurance" control={methods.control} />
        </div>
        <p className="block w-full md:hidden">{t("productsPallet.insuranceDescription")}</p>
      </div>
      <div className="shadow-basic flex w-full max-w-[800px] flex-col items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white p-6">
        <div className="flex w-full flex-1 flex-row items-center justify-between gap-4">
          <ShipmentTransitIcon className="text-zinc-900" />
          <div className="flex flex-1 flex-col">
            <p className="font-semibold">{t("productsPallet.expressTitle")}</p>
            <p className="hidden md:block">{t("productsPallet.expressDescription")}</p>
          </div>
          <StyledSwitch name="express" control={methods.control} />
        </div>
        <p className="block md:hidden">{t("productsPallet.expressDescription")}</p>
      </div>
    </div>
  );
};
